import Text from '@/components/ui/atoms/Text';
import Cluster from '@/components/ui/layout/Cluster';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedNumber, useIntl } from 'react-intl';
import styled from 'styled-components';
import { yupResolver } from '@hookform/resolvers/yup';
import { formatSecondsDuration } from '@/helpers';
import {
    useEpisodeEditAudioFileMutation,
    useEpisodeEditAudioFileState,
} from '@/context/EpisodeEditContext';
import { useParams } from 'react-router-dom';
import useEpisodeAudioFileSchema from '@/utils/hooks/schemas/useEpisodeAudioFileSchema.hook';
import EpisodeEditDeleteAudioFileModal from '../EpisodeEditDeleteAudioFileModal';
import useDeleteEpisodeAudioFileMutation from '@/queries/episode/useDeleteEpisodeAudioFileMutation.hook';
import { debounce } from 'lodash';
import IconAudioFile from '@/components/ui/icons/IconAudioFile';
import EpisodeEditResumeAudioFileMenu from '../EpisodeEditResumeAudioFileMenu';

const EpisodeEditResumeAudioFileUploaded = ({ file, audioUrl }) => {
    const intl = useIntl();
    const { episodeId } = useParams();
    const episodeEditAudioFileMutation = useEpisodeEditAudioFileMutation();
    const episodeDeleteAudioFileMutation = useDeleteEpisodeAudioFileMutation();

    const [_, setEpisodeEditAudioFile] = useEpisodeEditAudioFileState();
    const schema = useEpisodeAudioFileSchema();
    const { watch, handleSubmit, formState, setValue, setError } = useForm({
        mode: 'onChange',
        resolver: yupResolver(schema),
    });
    const { errors } = formState;
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const fileDuration = formatSecondsDuration(file?.duration);

    useEffect(() => {
        const subscription = watch(() => {
            handleSubmit(onSubmit)();
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    const handleOpenDeleteModal = () => setIsDeleteModalOpen(true);

    const onSubmit = ({ audioFile }) => {
        episodeEditAudioFileMutation.mutate(
            { episodeId, audioFile },
            {
                onSuccess: () => {
                    debounce(() => setEpisodeEditAudioFile(null), 300)();
                    window.localStorage.setItem(
                        `episode-form-${episodeId}-transcription-outdated-message`,
                        'true',
                    );
                },
                onError: () =>
                    setError('root.serverError', {
                        type: '400',
                        message: intl.formatMessage({ defaultMessage: 'Une erreur est survenue' }),
                    }),
            },
        );
        // Keep audioFile in a state to display audioFile data in
        // episode form during audioFile upload
        setEpisodeEditAudioFile(audioFile);
    };

    const handleDeleteFile = () => {
        episodeDeleteAudioFileMutation.mutate(
            { episodeId },
            {
                onSuccess: () => setIsDeleteModalOpen(false),
            },
        );
    };

    const handleEditFile = (file) => {
        setValue('audioFile', file, { shouldValidate: true, shouldDirty: true });
    };

    if (!file) return null;

    return (
        <>
            <Wrapper>
                <InnerWrapper>
                    <Cluster $gap="0.5rem" $align="center" $justify="space-between" $wrap="nowrap">
                        <IconAudioFile size="2rem" />
                        <FileInfoWrapper>
                            <Text
                                variant="bodyM"
                                fontWeight="--fw-semibold"
                                numberOfLines={1}
                                data-testid="episode-form-file-uploaded-name"
                            >
                                {file.originalName}
                            </Text>
                            <Cluster $gap="0.5rem" $align="center">
                                <Text
                                    variant="bodyM"
                                    color="--neutral500"
                                    numberOfLines={1}
                                    css="flex: none;"
                                >
                                    <FormattedNumber
                                        value={file.size}
                                        style="unit"
                                        unit="byte"
                                        unitDisplay="narrow"
                                        notation="compact"
                                    />
                                </Text>
                                <BulletIcon icon={icon({ name: 'circle', style: 'solid' })} />
                                <Text variant="bodyM" color="--neutral500" numberOfLines={1}>
                                    {fileDuration}
                                </Text>
                            </Cluster>
                        </FileInfoWrapper>
                    </Cluster>
                    <EpisodeEditResumeAudioFileMenu
                        handleDelete={handleOpenDeleteModal}
                        handleEdit={handleEditFile}
                        audioUrl={audioUrl}
                    />
                </InnerWrapper>

                {errors?.audioFile && (
                    <ErrorMessage>
                        <AlertIcon icon={icon({ name: 'exclamation-circle', style: 'solid' })} />
                        <Text color="--alert500">{errors?.audioFile?.message}</Text>
                    </ErrorMessage>
                )}
            </Wrapper>
            <EpisodeEditDeleteAudioFileModal
                isOpen={isDeleteModalOpen}
                onOpenChange={setIsDeleteModalOpen}
                handleDelete={handleDeleteFile}
                isLoading={episodeDeleteAudioFileMutation.isLoading}
            />
        </>
    );
};
const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 0.5rem;
    width: 100%;
`;
const InnerWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    column-gap: 0.5rem;
    width: 100%;
`;
const BulletIcon = styled(FontAwesomeIcon)`
    width: 0.25rem;
    height: 0.25rem;
    color: var(--neutral200);
    flex: none;
`;
const FileInfoWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    max-width: 250px;

    ${({ theme }) => theme.mediaQueries.mobile} {
        max-width: 200px;
    }
`;
const MenuItemIcon = styled(FontAwesomeIcon)`
    width: 0.75rem;
    height: 0.75rem;
`;
const AlertIcon = styled(MenuItemIcon)`
    color: var(--alert500);
`;
const ErrorMessage = styled.div`
    display: flex;
    align-items: center;
    column-gap: 0.5rem;
    width: 100%;
`;

export default EpisodeEditResumeAudioFileUploaded;
